/**
 * Process Flow designer (obj_type "L") — how work moves between objects.
 *
 * A diagram of a business, drawn out of five kinds of box and four kinds of
 * arrow, where the SHAPE says what a thing is and the COLOUR says what an arrow
 * does:
 *
 *   step      a screen where work is done — the chain a record travels
 *   entity    master data (customer, supplier, employee): a screen the process
 *             reads, beside the chain rather than in it
 *   store     data the process changes behind the scenes (stock, a ledger),
 *             naming the table AND the screen a person opens to see it
 *   process   a hand-off to a process drawn on its own diagram
 *   external  a party outside the application
 *
 *   work      the next step. Drawing it is how a work rule gets created, and
 *             whether it raises a task in people's worklists is a switch on the
 *             arrow rather than an invisible consequence of its colour
 *   link      a related record — an amendment to an order. Raises nothing
 *   update    this step writes that store
 *   read      this step looks that up
 *
 * Faint arrows are DERIVED — approval chains, lookups, cross-updates and
 * integration events read back out of the models. Read-only, so one glance
 * separates what a designer decided from what the metadata already implies.
 *
 * The diagram is an authoring surface, not a runtime: saving compiles each work
 * edge into a managed rule on its source object, and the engine reads only
 * those. Editing a rule here and editing it in the transaction designer are two
 * views of one file.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  ArrowLeft, Eye, EyeOff, LayoutGrid, Plus, Redo2, RotateCcw, Save, Undo2, Workflow,
} from 'lucide-react'

import {
  listProcessFlows, getProcessFlow, saveProcessFlow, deleteProcessFlow,
  processFlowGraph,
  type FlowDoc, type FlowEdge, type FlowFinding, type FlowNode, type FlowPalette,
  type NodeKind,
} from '@/api/processflow'
import DiscardChangesModal from '@/components/common/DiscardChangesModal'
import Modal from '@/components/common/Modal'
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard'
import { useDesignHistory } from '@/hooks/useDesignHistory'

import FlowCanvas from './FlowCanvas'
import FlowInspector from './FlowInspector'
import FlowPalette_ from './FlowPalette'
import IssuesPanel from './IssuesPanel'
import { EDGE_STYLE, GRID, KIND_STYLE, NODE_H, NODE_W, defaultEdgeKind, snap } from './shapes'

const BLANK = (n: number): FlowDoc => ({
  name: `process_${n}`, title: 'New process', description: '', nodes: [], edges: [],
})

type Selection = { kind: 'node' | 'edge'; id: string } | null

export default function ProcessFlowPage() {
  const { project = '' } = useParams()
  const navigate = useNavigate()

  const [flows, setFlows] = useState<Array<{ name: string; title: string }>>([])
  const [flow, setFlow] = useState<FlowDoc>(BLANK(1))
  /** The name the open flow was LOADED as. A rename has to take the old file
   *  and the rules it manages with it, and only the client knows what the
   *  designer started from. */
  const [openedAs, setOpenedAs] = useState('')
  const [derived, setDerived] = useState<{ nodes: FlowNode[]; edges: FlowEdge[] }>(
    { nodes: [], edges: [] })
  const [palette, setPalette] = useState<FlowPalette | null>(null)
  const [findings, setFindings] = useState<FlowFinding[]>([])
  const [selection, setSelection] = useState<Selection>(null)
  const [connecting, setConnecting] = useState(false)
  const [showDerived, setShowDerived] = useState(true)
  const [saving, setSaving] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  /** JSON of the last saved document. Dirty is a comparison, not a flag: a flag
   *  survives an undo back to the saved state and keeps claiming there is
   *  something to save. */
  const [saved, setSaved] = useState<string | null>(null)

  const isDirty = saved !== null && JSON.stringify(flow) !== saved
  const { guard, allowNextUnload, showDiscardModal, confirmDiscard, cancelDiscard } =
    useUnsavedChangesGuard(isDirty)
  const history = useDesignHistory(flow, setFlow)
  const { reset: resetHistory } = history

  const titles = useMemo(() => Object.fromEntries(
    (derived.nodes || []).map((n) => [n.obj_name, n.label || n.obj_name])
      .concat((palette?.objects || []).map((o) => [o.obj_name, o.label || o.obj_name])),
  ), [derived.nodes, palette])

  // ── load ────────────────────────────────────────────────────────────────
  const refreshList = useCallback(async () => {
    try {
      const res = await listProcessFlows(project)
      setFlows(res.flows || [])
      return res.flows || []
    } catch {
      // A palette with no list still lets a flow be drawn; the save says why.
      return []
    }
  }, [project])

  const openFlow = useCallback(async (name: string) => {
    try {
      const res = await getProcessFlow(project, name)
      setFlow(res.flow)
      setOpenedAs(res.flow.name)
      setSaved(JSON.stringify(res.flow))
      if (res.derived) setDerived(res.derived)
      if (res.palette) setPalette(res.palette)
      setFindings(res.review || [])
      setSelection(null)
      resetHistory()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not open that process.')
    }
  }, [project, resetHistory])

  useEffect(() => {
    void (async () => {
      const list = await refreshList()
      try {
        const g = await processFlowGraph(project)
        setDerived(g.derived || { nodes: [], edges: [] })
        setPalette(g.palette || null)
      } catch { /* an empty palette still lets a flow be drawn by hand */ }
      if (list.length) { void openFlow(list[0].name); return }
      // A project with no process yet opens on an empty one rather than a blank
      // screen with a button — there is nothing to decide first.
      const blank = BLANK(1)
      setFlow(blank)
      setSaved(JSON.stringify(blank))
      setOpenedAs('')
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project])

  // ── mutations ───────────────────────────────────────────────────────────
  /**
   * Always from the PREVIOUS value, never from the one captured at render. Two
   * palette clicks in the same tick both read the same nodes array otherwise,
   * and the second silently discards the first.
   */
  const patch = useCallback((next: Partial<FlowDoc> | ((f: FlowDoc) => Partial<FlowDoc>)) => {
    setFlow((f) => ({ ...f, ...(typeof next === 'function' ? next(f) : next) }))
  }, [])

  const place = useCallback((spec: {
    kind: NodeKind; obj_name: string; obj_type: string; label: string; flow?: string
  }) => {
    patch((f) => {
      const id = f.nodes.some((n) => n.id === spec.obj_name)
        ? `${spec.obj_name}_${Date.now().toString(36).slice(-4)}`
        : spec.obj_name
      // Lay the next box down where there is room, on the grid, rather than on
      // top of the last one.
      const i = f.nodes.length
      return {
        nodes: [...f.nodes, {
          id,
          kind: spec.kind,
          obj_name: spec.obj_name,
          obj_type: spec.obj_type,
          label: spec.label || spec.obj_name,
          ...(spec.flow ? { flow: spec.flow } : {}),
          x: snap(48 + (i % 4) * (NODE_W + 68)),
          y: snap(60 + Math.floor(i / 4) * (NODE_H + 92)),
        } as FlowNode],
      }
    })
  }, [patch])

  const removeNode = useCallback((id: string) => {
    patch((f) => ({
      nodes: f.nodes.filter((n) => n.id !== id),
      // An arrow to a removed box would compile to a rule pointing nowhere.
      edges: (f.edges || []).filter((e) => e.from !== id && e.to !== id),
    }))
    setSelection((s) => (s?.kind === 'node' && s.id === id ? null : s))
  }, [patch])

  const removeEdge = useCallback((id: string) => {
    patch((f) => ({ edges: (f.edges || []).filter((e) => e.id !== id) }))
    setSelection((s) => (s?.kind === 'edge' && s.id === id ? null : s))
  }, [patch])

  const connect = useCallback((from: string, to: string) => {
    if (from === to) return
    const id = `e${Date.now().toString(36)}`
    patch((f) => {
      const a = f.nodes.find((n) => n.id === from)
      const b = f.nodes.find((n) => n.id === to)
      if (!a || !b) return {}
      const kind = defaultEdgeKind((a.kind || 'step') as NodeKind, (b.kind || 'step') as NodeKind)
      const edge: FlowEdge = {
        id, kind, from, to, label: '',
        ...(kind === 'work' ? {
          raises_task: true,
          rule: {
            name: `${a.obj_name}_to_${b.obj_name}`.slice(0, 60).toLowerCase(),
            title: '',
            condition: '',
            assign: { roles: [] },
            target: { open: 'create', verb: '' },
          },
        } : {}),
      }
      return { edges: [...(f.edges || []), edge] }
    })
    setSelection({ kind: 'edge', id })
    setConnecting(false)
  }, [patch])

  const updateNode = useCallback((id: string, changes: Partial<FlowNode>) => {
    patch((f) => ({ nodes: f.nodes.map((n) => (n.id === id ? { ...n, ...changes } : n)) }))
  }, [patch])

  const updateEdge = useCallback((id: string, changes: Partial<FlowEdge>) => {
    patch((f) => ({ edges: (f.edges || []).map((e) => (e.id === id ? { ...e, ...changes } : e)) }))
  }, [patch])

  /** Rows and columns, so a diagram that has been dragged into a mess can be
   *  put back without moving twenty boxes by hand. */
  const tidy = useCallback(() => {
    patch((f) => {
      const order = ['step', 'entity', 'process', 'external', 'store']
      const lanes = new Map<string, FlowNode[]>()
      for (const n of f.nodes) {
        const k = (n.kind || 'step') as string
        lanes.set(k, [...(lanes.get(k) || []), n])
      }
      const out: FlowNode[] = []
      let row = 0
      for (const kind of order) {
        const lane = lanes.get(kind)
        if (!lane?.length) continue
        lane.forEach((n, i) => out.push({
          ...n,
          x: snap(48 + i * (NODE_W + 72)),
          y: snap(64 + row * (NODE_H + 84)),
        }))
        row += 1
      }
      for (const n of f.nodes) if (!out.some((o) => o.id === n.id)) out.push(n)
      return { nodes: out }
    })
  }, [patch])

  // ── save / discard / delete ─────────────────────────────────────────────
  const save = useCallback(async () => {
    setSaving(true)
    try {
      const res = await saveProcessFlow(project, flow, openedAs)
      const objs = Object.keys(res.compiled?.objects || {})
      setFindings(res.review || [])
      if (res.flow) {
        setFlow(res.flow)
        setSaved(JSON.stringify(res.flow))
        setOpenedAs(res.flow.name)
      } else {
        setSaved(JSON.stringify(flow))
        setOpenedAs(flow.name)
      }
      const errs = (res.review || []).filter((f) => f.severity === 'error').length
      const warns = (res.review || []).filter((f) => f.severity === 'warning').length
      toast.success(objs.length
        ? `Saved. Work rules written to ${objs.join(', ')}.`
        : 'Saved. No next-step arrows raise a task, so no rules were written.')
      // The count belongs in the toast; WHICH arrow belongs in the panel below
      // the canvas, where it can be clicked. Naming a number and leaving the
      // designer to find it is what this replaces.
      if (errs || warns) {
        toast(`${errs + warns} thing${errs + warns === 1 ? '' : 's'} to look at — `
          + 'listed under the diagram; click one to open it.',
        { icon: '⚠️', duration: 5000 })
      }
      void refreshList()
      return true
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save that process.')
      return false
    } finally { setSaving(false) }
  }, [flow, openedAs, project, refreshList])

  /** Put the document back to the last saved version. The first version had no
   *  way out of an edit at all. */
  const discard = useCallback(() => {
    if (saved === null) return
    const back = JSON.parse(saved) as FlowDoc
    setFlow(back)
    setSelection(null)
    resetHistory()
    toast.success('Changes discarded.')
  }, [saved, resetHistory])

  const doDelete = useCallback(async () => {
    setConfirmDelete(false)
    try {
      await deleteProcessFlow(project, flow.name)
      toast.success('Process deleted, along with the rules it managed.')
      const l = await refreshList()
      if (l.length) { void openFlow(l[0].name); return }
      const blank = BLANK(1)
      setFlow(blank)
      setSaved(JSON.stringify(blank))
      setOpenedAs('')
      setFindings([])
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete that process.')
    }
  }, [project, flow.name, refreshList, openFlow])

  // ── keyboard ────────────────────────────────────────────────────────────
  const selRef = useRef<Selection>(null)
  selRef.current = selection
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null
      const typing = !!el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault()
        void save()
        return
      }
      if (typing) return
      if ((e.key === 'Delete' || e.key === 'Backspace') && selRef.current) {
        e.preventDefault()
        if (selRef.current.kind === 'node') removeNode(selRef.current.id)
        else removeEdge(selRef.current.id)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [save, removeNode, removeEdge])

  // ── findings, indexed for the canvas ────────────────────────────────────
  const problems = useMemo(() => {
    const out: Record<string, 'error' | 'warning' | 'info'> = {}
    const rank = { error: 0, warning: 1, info: 2 }
    for (const f of findings) {
      for (const id of [f.edge_id, f.node_id]) {
        if (!id) continue
        const sev = (f.severity as 'error' | 'warning' | 'info') || 'info'
        if (!out[id] || rank[sev] < rank[out[id]]) out[id] = sev
      }
    }
    return out
  }, [findings])

  const placed = useMemo(
    () => new Set((flow.nodes || []).map((n) => n.flow || n.obj_name)),
    [flow.nodes])

  const goto = (fn: () => void) => guard(fn)

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100vh',
      background: 'var(--color-bg)', color: 'var(--color-text)',
    }}>
      {/* header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 9, padding: '0.6rem 0.9rem',
        borderBottom: '1px solid var(--color-border)',
        background: 'var(--color-bg-secondary)', flexWrap: 'wrap',
      }}>
        <button type="button" style={ghost} aria-label="Back"
          onClick={() => goto(() => { allowNextUnload(); navigate(-1) })}>
          <ArrowLeft size={15} />
        </button>
        <Workflow size={17} />
        <strong style={{ fontSize: '0.9825rem' }}>Process flows</strong>

        {/* The process is a FILTER over one canvas, the same affordance the
            runtime's Process Overview offers — so authoring and browsing read
            the same way, and an object in two processes is a special case in
            neither. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
          {flows.map((f) => (
            <button key={f.name} type="button" style={flow.name === f.name ? chipOn : chip}
              onClick={() => goto(() => { void openFlow(f.name) })}>
              {f.title || f.name}
            </button>
          ))}
          {!flows.some((f) => f.name === flow.name) && (
            <button type="button" style={chipOn}>{flow.title || flow.name} · unsaved</button>
          )}
          <button type="button" style={chip} onClick={() => goto(() => {
            const blank = BLANK(flows.length + 1)
            setFlow(blank)
            setOpenedAs('')
            setSaved(JSON.stringify(blank))
            setFindings([])
            setSelection(null)
            resetHistory()
          })}>
            <Plus size={11} /> New process
          </button>
        </div>

        <span style={{ flex: 1 }} />

        <button type="button" style={ghost} title="Undo (Ctrl+Z)"
          disabled={!history.canUndo} onClick={history.undo}>
          <Undo2 size={14} />
        </button>
        <button type="button" style={ghost} title="Redo (Ctrl+Shift+Z)"
          disabled={!history.canRedo} onClick={history.redo}>
          <Redo2 size={14} />
        </button>
        <button type="button" style={ghost} title="Tidy the boxes into rows" onClick={tidy}>
          <LayoutGrid size={14} />
        </button>
        <button type="button" style={ghost} onClick={() => setShowDerived((s) => !s)}
          title={showDerived
            ? 'Hide the faint arrows read out of the models'
            : 'Show the faint arrows read out of the models'}>
          {showDerived ? <Eye size={14} /> : <EyeOff size={14} />}
        </button>

        {isDirty && (
          <>
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-warning, #b45309)' }}>
              unsaved
            </span>
            <button type="button" style={ghost} onClick={discard} title="Put it back the way it was saved">
              <RotateCcw size={13} /> Discard
            </button>
          </>
        )}
        <button type="button" style={primary} disabled={saving}
          onClick={() => { void save() }}>
          <Save size={14} /> {saving ? 'Saving…' : 'Save & compile'}
        </button>
      </div>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <FlowPalette_ palette={palette} placed={placed} onPlace={place} />

        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <FlowCanvas
            flow={flow}
            derived={derived}
            showDerived={showDerived}
            selection={selection}
            problems={problems}
            titles={titles}
            connecting={connecting}
            onSelect={setSelection}
            onMoveNode={(id, x, y) => updateNode(id, { x, y })}
            onConnect={connect}
            onConnectingChange={setConnecting}
            onDeleteNode={removeNode}
          />
          <Legend />
          <IssuesPanel findings={findings} selection={selection} onSelect={setSelection} />
        </div>

        <FlowInspector
          project={project}
          flow={flow}
          palette={palette}
          selection={selection}
          titles={titles}
          onFlowChange={patch}
          onNodeChange={updateNode}
          onEdgeChange={updateEdge}
          onDeleteNode={removeNode}
          onDeleteEdge={removeEdge}
          onDeleteFlow={() => setConfirmDelete(true)}
          canDeleteFlow={flows.some((f) => f.name === flow.name)}
        />
      </div>

      <DiscardChangesModal
        open={showDiscardModal}
        onCancel={cancelDiscard}
        onDiscard={confirmDiscard}
        onSave={async () => { if (await save()) confirmDiscard() }}
        saving={saving}
        message="This process has changes that haven't been saved. Save them before leaving, or discard them?"
      />

      <Modal open={confirmDelete} onClose={() => setConfirmDelete(false)}
        title="Delete this process?">
        <p style={{ fontSize: '0.9125rem', lineHeight: 1.65, color: 'var(--color-text-secondary)' }}>
          <strong style={{ color: 'var(--color-text)' }}>{flow.title || flow.name}</strong> will be
          removed, and so will every work rule it wrote onto its objects — the tasks
          those rules raise stop being raised. Rules written by hand on the same
          objects are left alone.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
          <button type="button" className="btn btn-secondary"
            onClick={() => setConfirmDelete(false)}>Keep it</button>
          <button type="button" className="btn btn-danger"
            onClick={() => { void doDelete() }}>Delete the process</button>
        </div>
      </Modal>
    </div>
  )
}

/** What the shapes and the arrows mean, under the canvas where the picture is. */
function Legend() {
  return (
    <div style={{
      display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center',
      padding: '6px 12px', fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
      borderTop: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)',
    }}>
      {(Object.keys(KIND_STYLE) as Array<keyof typeof KIND_STYLE>).map((k) => (
        <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
          title={KIND_STYLE[k].hint}>
          <svg width="18" height="12" aria-hidden>
            {k === 'store' ? (
              <path d="M1 3 a8 3 0 0 1 16 0 v6 a8 3 0 0 1 -16 0 z" fill="none"
                stroke={KIND_STYLE[k].stroke} strokeWidth="1.3" />
            ) : (
              <rect x="1" y="1.5" width="16" height="9" rx={k === 'entity' ? 4.5 : 2}
                fill="none" stroke={KIND_STYLE[k].stroke} strokeWidth="1.3"
                strokeDasharray={k === 'external' ? '3 2' : undefined} />
            )}
          </svg>
          {KIND_STYLE[k].label}
        </span>
      ))}
      <span style={{ width: 1, height: 13, background: 'var(--color-border)' }} />
      {['work', 'link', 'update', 'read'].map((k) => (
        <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
          title={EDGE_STYLE[k].hint}>
          <svg width="22" height="8" aria-hidden>
            <line x1="0" y1="4" x2="16" y2="4" stroke={EDGE_STYLE[k].stroke}
              strokeWidth={EDGE_STYLE[k].width} strokeDasharray={EDGE_STYLE[k].dash} />
            <path d="M16 1.4 L21 4 L16 6.6 z" fill={EDGE_STYLE[k].stroke} />
          </svg>
          {EDGE_STYLE[k].label}
        </span>
      ))}
      <span style={{ marginLeft: 'auto', opacity: 0.8 }}>
        grid {GRID}px · scroll to zoom · drag the background to pan
      </span>
    </div>
  )
}

const primary: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
  padding: '0.4rem 0.8rem', borderRadius: 7, fontSize: '0.8725rem', fontWeight: 600,
  border: '1px solid var(--color-accent)', background: 'var(--color-accent)', color: '#fff',
}
const ghost: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer',
  padding: '0.3rem 0.55rem', borderRadius: 6, fontSize: '0.8325rem',
  border: '1px solid var(--color-border)', background: 'var(--color-input-bg, #fff)',
  color: 'var(--color-text)',
}
const chip: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer',
  padding: '0.24rem 0.6rem', borderRadius: 6, fontSize: '0.8225rem',
  border: '1px solid var(--color-border)', background: 'var(--color-input-bg, #fff)',
  color: 'var(--color-text)',
}
const chipOn: React.CSSProperties = {
  ...chip, background: 'var(--color-accent)', borderColor: 'var(--color-accent)',
  color: '#fff', fontWeight: 600,
}
